import classnames from 'classnames/bind';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  Legend,
  LinearScale,
  Tooltip,
  TimeSeriesScale,
} from 'chart.js';
import 'chartjs-adapter-luxon';

import { DataWidget } from 'types/dashboard_widget';

import { TIME_UNIT_MAP } from 'dashboards/types/time_unit';
import { resolveGranularity } from 'dashboards/services/granularity';
import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { rowsToTimezone } from 'dashboards/services/table';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useGroupedBarChart } from 'dashboards/hooks/useGroupedBarChart';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { Icon } from 'components/Icon/IconDeprecated';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

ChartJS.register(BarElement, Legend, LinearScale, Tooltip, TimeSeriesScale);

type StackedBarChartProps = {
  widget:
    | DataWidget<'timeseries-bar-stacked'>
    | DataWidget<'timeseries-bar-grouped'>;
  variant: 'stacked' | 'grouped';
};

export function StackedBarChart({ widget, variant }: StackedBarChartProps) {
  const { metric, granularity: rawGranularity, ...query } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const granularity = resolveGranularity(rawGranularity, { start, stop });

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const { data, isLoading, error } = useGroupedBarChart(
    {
      metricName: metric,
      timeRange: { start, stop },
      timeZone: timezone,
      granularity,
      ...query,
      filters,
    },
    variant
  );

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError error={error.message} />
      </Widget>
    );
  }

  if (!data) {
    return null;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'timeseries' as const,
        time: {
          unit: TIME_UNIT_MAP[granularity],
        },
        adapters: {
          date: {
            zone: timezone,
          },
        },
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        // Hide legend when it's an empty graph
        display: data.datasets.some(({ data }) =>
          data.some((value) => value > 0)
        ),
        labels: { useBorderRadius: true },
      },
    },
  };

  const csv = {
    headers: ['timestamp', ...data.datasets.map((dataset) => dataset.label)],
    rows: data.labels.map((timestamp, i) => [
      timestamp,
      ...data.datasets.map((dataset) => String(dataset.data[i] ?? 0)),
    ]),
  };

  const menuItems = {
    csv: {
      children: 'Export as CSV',
      leftSection: <Icon name="export" size="small" />,
      onClick: () =>
        downloadFile(
          widget.name,
          createCsvFile(rowsToTimezone({ ...csv, timezone }))
        ),
      disabled: csv.rows.length === 0,
    },
  };

  return (
    <Widget widget={widget} menuItems={menuItems}>
      <div className={c('chart')}>
        <Bar data={data} options={options} />
      </div>
    </Widget>
  );
}
